import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import { get } from "../../../../utils/api";
import { fixedTons, formatNumberCommaSeparated } from "../../../../utils/common";
import useAuthStore from "../../../../store/ContextUser";
import PieChart from "../../../../Components/GraphNCharts/Pie";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import NoDataFound from "../../../../Components/Masters/NoDataFound";

const Dashboard = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const { selectedCompany } = useAuthStore();

  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentMonth,
    year: currentYear,
  });
  const [dashboardData, setDashboardData] = useState({
    totalEntries: 0,
    totalEmission: 0,
  });
  const [emissionBreakdown, setEmissionBreakdown] = useState({});
  const [selectedEmissionSection, setSelectedEmissionSection] = useState(
    "purchasedFuels",
  );

  const yearList = useMemo(() => {
    const years = [];
    for (let year = 2020; year <= currentYear; year += 1) years.push(year);
    return years;
  }, [currentYear]);

  const getNumeric = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const fetchDashboardData = async () => {
    try {
      const hasFromDate =
        fromDate?.month !== undefined &&
        fromDate?.month !== null &&
        fromDate?.year !== undefined &&
        fromDate?.year !== null &&
        String(fromDate?.month).trim() !== "" &&
        String(fromDate?.year).trim() !== "";
      const hasToDate =
        toDate?.month !== undefined &&
        toDate?.month !== null &&
        toDate?.year !== undefined &&
        toDate?.year !== null &&
        String(toDate?.month).trim() !== "" &&
        String(toDate?.year).trim() !== "";

      if (!hasFromDate || !hasToDate) {
        setDashboardData({ totalEntries: 0, totalEmission: 0 });
        return;
      }

      const listParams = new URLSearchParams({
        startYear: fromDate?.year || "",
        startMonth: fromDate?.month || "",
        endYear: toDate?.year || "",
        endMonth: toDate?.month || "",
      });

      const res = await get(`/scope3/fuelEnergyActivities/pageData?${listParams.toString()}`);
      const pageData = res?.data?.data || {};
      const breakdown = pageData?.emissionBreakdown || {};

      setDashboardData({
        totalEntries: getNumeric(pageData?.totalEntries),
        totalEmission: getNumeric(pageData?.totalEmission),
      });
      setEmissionBreakdown(breakdown);
    } catch (error) {
      console.error("Error fetching fuel and energy dashboard data:", error);
      setDashboardData({ totalEntries: 0, totalEmission: 0 });
      setEmissionBreakdown({});
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  const splitData = useMemo(() => {
    const partA = emissionBreakdown?.partA || {};
    const partB = emissionBreakdown?.partB || {};
    const partC = emissionBreakdown?.partC || {};
    const partD = emissionBreakdown?.partD || {};

    const items = [
      {
        label: "Purchased Fuels",
        value: getNumeric(partA?.total ?? partA?.fuelTotal ?? 0),
        color: "#3DDC84",
      },
      {
        label: "Purchased Electricity",
        value: getNumeric(partB?.total ?? 0),
        color: "#0052CC",
      },
      {
        label: "Purchased Electricity Resold Quantity",
        value: getNumeric(partD?.total ?? 0),
        color: "#FFB000",
      },
      {
        label: "Transmission and Distribution losses",
        value: getNumeric(partC?.total ?? 0),
        color: "#FF4C4C",
      },
    ].filter((item) => item.value > 0);

    return {
      total: items.reduce((acc, item) => acc + item.value, 0),
      labels: items.map((item) => item.label),
      values: items.map((item) => item.value),
      colors: items.map((item) => item.color),
      items,
    };
  }, [emissionBreakdown]);

  const sectorEmissionData = useMemo(() => {
    const partA = emissionBreakdown?.partA || {};
    const partB = emissionBreakdown?.partB || {};
    const partC = emissionBreakdown?.partC || {};
    const partD = emissionBreakdown?.partD || {};
    const fuel = partA?.fuel || {};

    const sections = {
      purchasedFuels: [
        ["Natural gas", fuel.naturalGas],
        ["Coal", fuel.coal],
        ["Petrol", fuel.petroleumGasoline],
        ["Diesel", fuel.diesel],
        ["Fuel Oil", fuel.fuelOil],
        ["LPG", fuel.lpg],
        ["Propane", fuel.propane],
        ["CNG", fuel.cng],
        ["Heating and Cooling", partA.heatingAndCooling],
        ["Steam", partA.steam],
      ],
      purchasedElectricity: [["Purchased Electricity", partB.total]],
      transmissionAndDistributionLosses: [
        ["Transmission and Distribution losses", partC.total],
      ],
      purchasedElectricityResoldQuantity: [
        ["Purchased Electricity Resold Quantity", partD.total],
      ],
    };
    const rows = sections[selectedEmissionSection] || sections.purchasedFuels;
    const colors = ["#ff4c4c", "#8f00b8", "#ffb52e", "#0052b8", "#4a4b55", "#2bd516", "#f27d00", "#12a8a8"];
    const visibleRows = rows
      .map(([label, value], index) => ({
        label,
        value: getNumeric(value),
        color: colors[index % colors.length],
      }))
      .filter((row) => row.value > 0);

    return {
      labels: visibleRows.map((row) => row.label),
      datasets: [
        {
          label: "Emission",
          data: visibleRows.map((row) => row.value),
          backgroundColor: visibleRows.map((row) => row.color),
          borderWidth: 0,
        },
      ],
    };
  }, [emissionBreakdown, selectedEmissionSection]);

  const fuelEnergySectionChart = useMemo(() => {
    const partA = emissionBreakdown?.partA || {};
    const partB = emissionBreakdown?.partB || {};
    const partC = emissionBreakdown?.partC || {};
    const partD = emissionBreakdown?.partD || {};
    const fuel = partA?.fuel || {};
    const rows = [
      ["CNG", fuel.cng],
      ["LPG", fuel.lpg],
      ["Natural Gas", fuel.naturalGas],
      ["Propane", fuel.propane],
      ["Diesel", fuel.diesel],
      ["Petrol", fuel.petroleumGasoline],
      ["Coal", fuel.coal],
      ["Fuel Oil", fuel.fuelOil],
      ["Heating and Cooling", partA.heatingAndCooling],
      ["Steam", partA.steam],
      ["Purchased Electricity", partB.total],
      ["T & D Losses", partC.total],
      ["Electricity Resold", partD.total],
    ];
    const barColor = "#0052b8";

    return {
      labels: rows.map(([label]) => label),
      datasets: [
        {
          label: "Emission",
          data: rows.map(([, value]) => fixedTons(Number(value) || 0)),
          backgroundColor: barColor,
          borderColor: barColor,
          borderWidth: 0,
        },
      ],
    };
  }, [emissionBreakdown]);

  const hasEmissionPerSectionData = fuelEnergySectionChart.datasets[0].data.some(
    (value) => Number(value) > 0
  );

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Fuel and Energy Related Activities</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
            fixedPeriodMode: false,
            facilityRequired: "false",
          }}
        />
      </div>

      <div className={styles.purchase_container} style={{ paddingTop: "0.5rem" }}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <p className={styles.graph_heading}>Total Emissions (tCO2e)</p>
          <p className={styles.graph_value}>
            {formatNumberCommaSeparated(fixedTons(dashboardData.totalEmission))}
          </p>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <p className={styles.graph_heading}>Emission Split</p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 1fr)",
              gap: "1rem",
              alignItems: "center",
              marginTop: "0.75rem",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: splitData.labels.length > 0 ? "340px" : "none",
                aspectRatio: splitData.labels.length > 0 ? "1" : "auto",
                height: splitData.labels.length > 0 ? "auto" : "340px",
                margin: "0 auto",
                gridColumn: splitData.labels.length > 0 ? "auto" : "1 / -1",
              }}
            >
              {splitData.labels.length > 0 ? (
                <PieChart
                  labels={splitData.labels}
                  customHeight="100%"
                  customWidth="100%"
                  useEchartsLabelLayout
                  showBorder={false}
                  dataset={[
                    {
                      data: splitData.values,
                      backgroundColor: splitData.colors,
                    },
                  ]}
                  hide={false}
                />
              ) : (
                <div
                  style={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <NoDataFound mt={0} />
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "0.9rem",
              }}
            >
              {splitData.items.map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: item.color,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: "14px", fontWeight: 500 }}>
                      {item.label}
                    </span>
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 500 }}>
                    {formatNumberCommaSeparated(fixedTons(item.value))} tCO2e
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <p className={styles.graph_heading}>Emission per section (tCO2e)</p>
          {hasEmissionPerSectionData ? (
            <BarChart
              datasets={fuelEnergySectionChart.datasets}
              labels={fuelEnergySectionChart.labels}
              display={false}
              yAxisTitle="Emission (tCO2e)"
              ellipsis={false}
              customHeight="320px"
              linearTickFormatter={(value) =>
                formatNumberCommaSeparated(fixedTons(value))
              }
            />
          ) : (
            <div
              style={{
                height: "320px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoDataFound mt={0} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
